/**
 * Rest mechanics (2024 rules). Pure functions shared by the API and the UI so a
 * rest preview on screen matches exactly what the server will apply.
 */
import type { Ability, ClassKey } from './constants'
import { CLASSES } from './constants'
import { abilityModifier, clampLevel, spellcasting, type CalcInput } from './calc'

export type RestType = 'short' | 'long'

export interface RestState {
  level: number
  class: string
  scores: Record<Ability, number>
  hpMax: number
  hpCurrent: number
  hpTemp: number
  hitDiceTotal: number
  hitDiceSpent: number
  exhaustion: number
  slotsUsed: Record<string, number>
  concentratingOn: string | null
  deathSaves: { successes: number; failures: number }
}

export interface RestResult {
  changes: Partial<RestState>
  /** Plain-language list of what the rest did, for the confirmation. */
  summary: string[]
}

const isPactCaster = (classKey: string): boolean =>
  CLASSES[classKey as ClassKey]?.casting === 'pact'

/**
 * A short rest restores pact magic slots and nothing else automatically —
 * hit dice are spent deliberately, one at a time, not swept up by the rest.
 */
export function applyShortRest(state: RestState): RestResult {
  const changes: Partial<RestState> = {}
  const summary: string[] = []

  if (isPactCaster(state.class) && Object.values(state.slotsUsed).some((n) => n > 0)) {
    changes.slotsUsed = {}
    summary.push('Pact magic slots came back')
  }

  // Concentration cannot survive an hour of sitting still.
  if (state.concentratingOn) {
    changes.concentratingOn = null
    summary.push('Concentration ended')
  }

  if (summary.length === 0) {
    summary.push('Nothing recharges on a short rest for this character — spend hit dice to heal')
  }
  return { changes, summary }
}

export function applyLongRest(state: RestState): RestResult {
  const changes: Partial<RestState> = {}
  const summary: string[] = []

  if (state.hpCurrent < state.hpMax) {
    changes.hpCurrent = state.hpMax
    summary.push(`Hit points back to ${state.hpMax}`)
  }
  if (state.hpTemp > 0) {
    changes.hpTemp = 0
    summary.push('Temporary hit points expired')
  }

  // You regain up to half your total hit dice, minimum one.
  if (state.hitDiceSpent > 0) {
    const regained = Math.min(state.hitDiceSpent, Math.max(1, Math.floor(state.hitDiceTotal / 2)))
    changes.hitDiceSpent = state.hitDiceSpent - regained
    summary.push(`Regained ${regained} hit ${regained === 1 ? 'die' : 'dice'}`)
  }

  if (Object.values(state.slotsUsed).some((n) => n > 0)) {
    changes.slotsUsed = {}
    summary.push('All spell slots restored')
  }

  if (state.exhaustion > 0) {
    changes.exhaustion = state.exhaustion - 1
    summary.push(`Exhaustion down to ${state.exhaustion - 1}`)
  }

  if (state.concentratingOn) {
    changes.concentratingOn = null
    summary.push('Concentration ended')
  }

  if (state.deathSaves.successes > 0 || state.deathSaves.failures > 0) {
    changes.deathSaves = { successes: 0, failures: 0 }
    summary.push('Death saves cleared')
  }

  if (summary.length === 0) summary.push('Already fully rested')
  return { changes, summary }
}

export const applyRest = (state: RestState, type: RestType): RestResult =>
  type === 'long' ? applyLongRest(state) : applyShortRest(state)

/** Total slots available at this level, from the class's progression. */
export function slotsFor(state: Pick<RestState, 'class' | 'level' | 'scores'>): Record<number, number> {
  const input: CalcInput = {
    level: state.level,
    class: state.class,
    scores: state.scores,
    saveProficiencies: [],
    skillProficiencies: {},
    armorClass: 10,
    acIsManual: true,
    initiativeBonus: 0,
  }
  return spellcasting(input)?.slots ?? {}
}

export interface HitDieSpend {
  rolled: number
  healed: number
  hpCurrent: number
  hitDiceSpent: number
}

/**
 * Spends one hit die. The caller supplies the roll so it can come from the
 * shared dice engine and appear in the DM's feed like any other roll.
 */
export function spendHitDie(state: RestState, rolled: number): HitDieSpend | null {
  if (state.hitDiceSpent >= state.hitDiceTotal) return null

  const conModifier = abilityModifier(state.scores.con)
  // A hit die never heals for less than nothing, even with a negative CON.
  const healed = Math.max(0, rolled + conModifier)

  return {
    rolled,
    healed,
    hpCurrent: Math.min(state.hpMax, state.hpCurrent + healed),
    hitDiceSpent: state.hitDiceSpent + 1,
  }
}

export const hitDieFor = (classKey: string): number =>
  CLASSES[classKey as ClassKey]?.hitDie ?? 8

/** Levels at which the 2024 rules hand you a decision rather than just numbers. */
export function levelUpChoices(classKey: string, newLevel: number): string[] {
  const prompts: string[] = []
  const level = clampLevel(newLevel)

  if (level === 3) prompts.push('Choose your subclass')
  if ([4, 8, 12, 16, 19].includes(level)) {
    prompts.push('Take an Ability Score Improvement (+2 to one ability, or +1 to two) or a feat')
  }
  if (level === 5) prompts.push('Extra Attack or a big spell tier — check your class table')

  const def = CLASSES[classKey as ClassKey]
  if (def?.casting === 'full' && [3, 5, 7, 9].includes(level)) {
    prompts.push('You have a new tier of spell slots')
  }
  return prompts
}
